'use client'

import { useState, useCallback } from 'react'
import { Input } from '@/ui/input'
import { Textarea } from '@/ui/textarea'
import { Button } from '@/ui/button'
import { useCreateProject } from './write-spec'
import type { ProjectStatus } from '@/document'

interface ProjectCreateFormProps {
  visible: boolean
  onClose: () => void
}

export function ProjectCreateForm({
  visible,
  onClose,
}: ProjectCreateFormProps) {
  const [name, setName] = useState('')
  const [description, setDescription] = useState('')
  const createProject = useCreateProject()

  const handleSubmit = useCallback(
    (e: React.FormEvent) => {
      e.preventDefault()
      const trimmed = name.trim()
      if (!trimmed) return

      createProject.mutate(
        {
          name: trimmed,
          description: description.trim(),
          status: 'active' satisfies ProjectStatus,
        },
        {
          onSuccess: () => {
            setName('')
            setDescription('')
            onClose()
          },
        },
      )
    },
    [name, description, createProject, onClose],
  )

  if (!visible) return null

  return (
    <form
      onSubmit={handleSubmit}
      className="border-border/50 bg-muted/30 space-y-3 rounded-lg border p-4"
      aria-label="Create new project"
    >
      <Input
        value={name}
        onChange={(e) => setName(e.target.value)}
        placeholder="Project name"
        aria-label="Project name"
        autoFocus
        required
        className="text-sm"
      />
      <Textarea
        value={description}
        onChange={(e) => setDescription(e.target.value)}
        placeholder="Description (optional)"
        aria-label="Project description"
        rows={2}
        className="min-h-0 text-sm sm:min-h-0"
      />
      <div className="flex gap-2">
        <Button
          type="submit"
          size="sm"
          disabled={!name.trim() || createProject.isPending}
        >
          {createProject.isPending ? 'Creating...' : 'Create'}
        </Button>
        <Button type="button" variant="ghost" size="sm" onClick={onClose}>
          Cancel
        </Button>
      </div>
    </form>
  )
}
